import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { OnboardingChecklistResponse, StartHereTarget } from '@bower/shared';
import { useModalCreateOpeners } from '../../hooks/create/useModalCreateOpeners';
import { useMobileDetection } from '../../hooks/ui/useMobileDetection';
import { useRecordExplicitCompletion } from '../../hooks/data/useOnboardingChecklist';
import { openBirdChat } from '../../services/events/birdChatEvents';
import { safeAnalytics } from '../../utils/analytics';
import { getArtifactRoute } from '../../utils/artifactCapabilities';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { ROUTES } from '../../constants/routes';
import type { ChecklistStepAction, ChecklistStepView } from './checklistSteps';

type Surface = 'dashboard_onboarding' | 'onboarding_checklist';

/**
 * What each unified setup step does, shared by the dashboard list and the rail
 * panel so one click means one thing on both. `onPhone` is the surface's own
 * QR reveal (a modal on the dashboard, the panel's own on the rail); on a
 * phone there is nothing to scan, so the step opens capture directly.
 */
export function useChecklistStepActions(
  checklist: OnboardingChecklistResponse,
  surface: Surface,
  onPhone: () => void,
) {
  const navigate = useNavigate();
  const { isMobile } = useMobileDetection();
  const create = useModalCreateOpeners(undefined, surface);
  const recordExplicit = useRecordExplicitCompletion();
  const variant = checklist.unified?.variant;
  const alreadyOpened = checklist.completions.open_start_here;

  /** Record the Start here tile as opened; the link itself navigates. */
  const recordStartHere = useCallback(
    (source: StartHereTarget['source']) => {
      safeAnalytics.track(ANALYTICS_EVENTS.ONBOARDING_START_HERE_OPENED, { source, variant });
      if (!alreadyOpened) recordExplicit.mutate('start_here_opened');
    },
    [alreadyOpened, recordExplicit, variant],
  );

  const startHere = checklist.unified?.startHere ?? null;

  const actions = useMemo<Record<ChecklistStepAction, () => void>>(
    () => ({
      ask: () => openBirdChat({ source: surface, freshContext: true }),
      start_here: () => {
        const first = startHere?.items[0];
        const href = first ? getArtifactRoute(first.artifactType, first.artifactId) : null;
        if (!startHere || !href) return;
        recordStartHere(startHere.source);
        navigate(href);
      },
      import: create.onOpenImport,
      run_protocol: () => navigate(ROUTES.protocols),
      equipment: create.onOpenEquipment,
      phone: isMobile ? () => navigate(ROUTES.mobileCapture) : onPhone,
      connect: () => navigate(`${ROUTES.settings}?tab=connectors`),
      invite: () => window.dispatchEvent(new CustomEvent('onboarding:openInvite')),
    }),
    [create, isMobile, navigate, onPhone, recordStartHere, startHere, surface],
  );

  // The label follows the action: on a phone the phone step opens capture,
  // so it says so (as the per-user list's "Capture now" does).
  const labelFor = useCallback(
    (step: ChecklistStepView) =>
      step.action === 'phone' && isMobile ? 'Capture now' : step.ctaLabel,
    [isMobile],
  );

  return { actions, recordStartHere, labelFor };
}
